/**
 * Shared FFmpeg scene/shot-change detection with:
 * - Downscale + light blur + mild contrast/gamma (dark cuts without amplifying action)
 * - Low scene floor so more candidates emit scores
 * - JS peak-vs-sustained-motion post-processing (rejects pans/action plateaus)
 *
 * Single decode pass; typically same speed or faster than full-res select=scene.
 *
 * Note: modern FFmpeg showinfo does NOT print scene_score. Scores come from
 * `metadata=print` as `lavfi.scene_score` on the line after `pts_time`.
 */

import { loadFfmpeg } from "@app/lib/loadFfmpeg.js";

/** FFmpeg candidate floor — keep low so post-process sees fade ramps */
const SCENE_FLOOR = 0.05;

const PREPROCESS = "scale=640:-2,gblur=sigma=1,eq=contrast=1.15:gamma=1.1";

/**
 * Low / Medium / High UI + AI default.
 * Mapped to post-process sensitivity (not raw FFmpeg gt(scene,*) alone).
 */
export const SENSITIVITY_PRESETS = {
    high: {
        cutThreshold: 0.22,
        fadeFloor: 0.07,
        fadeMinDuration: 0.25,
        minGap: 0.3,
        clusterGap: 0.2,
        peakRatio: 1.6,
        fadePeakRatio: 1.35,
    },
    medium: {
        cutThreshold: 0.32,
        fadeFloor: 0.09,
        fadeMinDuration: 0.35,
        minGap: 0.5,
        clusterGap: 0.2,
        peakRatio: 1.8,
        fadePeakRatio: 1.45,
    },
    low: {
        cutThreshold: 0.42,
        fadeFloor: 0.11,
        fadeMinDuration: 0.45,
        minGap: 1.0,
        clusterGap: 0.2,
        peakRatio: 2.0,
        fadePeakRatio: 1.55,
    },
    /** Former AI fixed threshold ~0.2 with 1s gap */
    ai: {
        cutThreshold: 0.26,
        fadeFloor: 0.08,
        fadeMinDuration: 0.3,
        minGap: 1.0,
        clusterGap: 0.2,
        peakRatio: 1.75,
        fadePeakRatio: 1.4,
    },
};

/**
 * Map legacy numeric FFmpeg thresholds (0.15 / 0.35 / 0.5) to named presets.
 * @param {number|string} level
 * @returns {'high'|'medium'|'low'|'ai'}
 */
export function resolveSensitivity(level) {
    if (typeof level === "string" && SENSITIVITY_PRESETS[level]) {
        return level;
    }
    const n = Number(level);
    if (n <= 0.2) return "high";
    if (n <= 0.3) return "ai";
    if (n <= 0.4) return "medium";
    return "low";
}

/**
 * Median of a numeric array (mutates a copy via sort).
 * @param {number[]} values
 */
function median(values) {
    if (!values.length) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    if (sorted.length % 2 === 0) {
        return (sorted[mid - 1] + sorted[mid]) / 2;
    }
    return sorted[mid];
}

/**
 * Collapse nearby candidates into hard cuts and fade midpoints.
 * Rejects sustained elevated runs typical of pans / fast action.
 * @param {Array<{time: number, score: number}>} candidates
 * @param {typeof SENSITIVITY_PRESETS.medium} preset
 * @returns {Array<{time: number, score: number, type: 'cut'|'fade'}>}
 */
export function postProcessSceneScores(candidates, preset) {
    if (!candidates?.length) return [];

    const sorted = [...candidates].sort((a, b) => a.time - b.time);
    const {
        cutThreshold,
        fadeFloor,
        fadeMinDuration,
        minGap,
        clusterGap,
        peakRatio,
        fadePeakRatio,
    } = preset;

    const rawShots = [];
    let i = 0;

    while (i < sorted.length) {
        if (sorted[i].score < fadeFloor) {
            i++;
            continue;
        }

        const startIdx = i;
        let peakIdx = i;

        while (
            i + 1 < sorted.length &&
            sorted[i + 1].score >= fadeFloor &&
            sorted[i + 1].time - sorted[i].time <= clusterGap
        ) {
            i++;
            if (sorted[i].score > sorted[peakIdx].score) {
                peakIdx = i;
            }
        }

        const run = sorted.slice(startIdx, i + 1);
        const peak = sorted[peakIdx];
        const runDuration = sorted[i].time - sorted[startIdx].time;

        const nonPeakScores = run
            .filter((_, idx) => startIdx + idx !== peakIdx)
            .map((c) => c.score);
        const baseline = nonPeakScores.length
            ? median(nonPeakScores)
            : fadeFloor;
        const prominence = peak.score / Math.max(baseline, fadeFloor);

        const isHardCut =
            peak.score >= cutThreshold && prominence >= peakRatio;
        const isFade =
            runDuration >= fadeMinDuration &&
            peak.score >= cutThreshold * 0.85 &&
            prominence >= fadePeakRatio;

        if (isHardCut || isFade) {
            rawShots.push({
                time: peak.time + 0.001,
                score: peak.score,
                type: isHardCut ? "cut" : "fade",
            });
        }

        i++;
    }

    return dedupeByMinGap(rawShots, minGap);
}

/**
 * Keep higher-scoring shot when two fall within minGap.
 * @param {Array<{time: number, score: number, type?: string}>} shots
 * @param {number} minGap
 */
function dedupeByMinGap(shots, minGap) {
    if (!shots.length) return [];
    const out = [shots[0]];
    for (let i = 1; i < shots.length; i++) {
        const prev = out[out.length - 1];
        const cur = shots[i];
        if (cur.time - prev.time < minGap) {
            if (cur.score > prev.score) {
                out[out.length - 1] = cur;
            }
        } else {
            out.push(cur);
        }
    }
    return out;
}

/**
 * Stateful stderr parser for select + metadata=print (and legacy showinfo).
 * metadata=print emits pts_time and lavfi.scene_score on consecutive lines.
 */
function createStderrCandidateParser(onCandidate) {
    let pendingTime = null;

    return function parseStderrLine(stderrLine) {
        if (!stderrLine) return;

        const ptsMatch = stderrLine.match(/pts_time:([0-9.]+)/);
        if (ptsMatch) {
            pendingTime = parseFloat(ptsMatch[1]);
        }

        const lavfiScore = stderrLine.match(/lavfi\.scene_score=([0-9.]+)/);
        if (lavfiScore && pendingTime != null) {
            onCandidate({
                time: pendingTime,
                score: parseFloat(lavfiScore[1]),
            });
            pendingTime = null;
            return;
        }

        // Legacy showinfo path: scene_score on the same line as pts_time (older FFmpeg)
        const inlineScore = stderrLine.match(/scene_score=([0-9.]+)/);
        if (inlineScore && pendingTime != null && stderrLine.includes("showinfo")) {
            onCandidate({
                time: pendingTime,
                score: parseFloat(inlineScore[1]),
            });
            pendingTime = null;
            return;
        }

        // showinfo without score: frame already passed select — treat as a hard cut
        if (pendingTime != null && stderrLine.includes("showinfo") && /n:\s*\d+/.test(stderrLine)) {
            onCandidate({
                time: pendingTime,
                score: 1,
            });
            pendingTime = null;
        }
    };
}

/**
 * Detect shot changes in a media file.
 *
 * @param {object} options
 * @param {string} options.inputPath
 * @param {number} [options.startSec]
 * @param {number} [options.durationSec]
 * @param {number|string} [options.sensitivity='medium'] Named preset or legacy numeric threshold
 * @param {(cmd: string) => void} [options.onStart]
 * @param {(data: object) => void} [options.onCodecData]
 * @param {(prog: object) => void} [options.onProgress]
 * @returns {Promise<Array<{time: number, score: number, type: string}>>}
 */
export function detectSceneChanges({
    inputPath,
    startSec,
    durationSec,
    sensitivity = "medium",
    onStart,
    onCodecData,
    onProgress,
} = {}) {
    const presetKey = resolveSensitivity(sensitivity);
    const preset = SENSITIVITY_PRESETS[presetKey];
    const ffmpeg = loadFfmpeg();

    return new Promise((resolve, reject) => {
        const candidates = [];
        const parseStderr = createStderrCandidateParser((candidate) => {
            candidates.push(candidate);
        });
        const cmd = ffmpeg(inputPath);

        if (startSec != null && startSec > 0) cmd.seekInput(startSec);
        if (durationSec != null && durationSec > 0) cmd.duration(durationSec);

        // metadata=print exposes lavfi.scene_score (showinfo alone does not on current FFmpeg)
        const filter = `${PREPROCESS},select='gt(scene,${SCENE_FLOOR})',metadata=print`;

        cmd.videoFilters(filter)
            .outputOptions("-f null")
            .output("scene-detect")
            .on("start", (commandLine) => {
                if (onStart) onStart(commandLine);
            })
            .on("codecData", (data) => {
                if (onCodecData) onCodecData(data);
            })
            .on("progress", (prog) => {
                if (onProgress) onProgress(prog);
            })
            .on("stderr", (stderrLine) => {
                parseStderr(stderrLine);
            })
            .on("end", () => {
                resolve(postProcessSceneScores(candidates, preset));
            })
            .on("error", (err) => {
                reject(err);
            })
            .run();
    });
}
